//realtimeModel.ts — the Decart models the Worker mints for, and the mirror modes built on them

//The token allowlist: a tampered request cannot mint for anything outside it.
export const DECART_MODELS = ['lucy-vton-3.5', 'lucy-2.5', 'lucy-restyle-2'] as const

export type DecartModel = (typeof DECART_MODELS)[number]

export const isDecartModel = (value: unknown): value is DecartModel =>
	DECART_MODELS.includes(value as DecartModel)

//Decart's published 720p rates. Billing is per second of stream, whatever happens in it.
export const PRICE_PER_SECOND_USD: Record<DecartModel, number> = {
	'lucy-vton-3.5': 0.02,
	'lucy-2.5': 0.02,
	'lucy-restyle-2': 0.01,
}

//What a session runs. Character is lucy-2.5 steered by a reference image, so it is a mode,
//not a model.
export type MirrorMode = 'lucy-vton-3.5' | 'character' | 'lucy-2.5' | 'lucy-restyle-2'

//The mode the admin's live catalogue implies: characters, or garments to try on.
export type CatalogMode = Extract<MirrorMode, 'lucy-vton-3.5' | 'character'>

//The gear menu's choice. Auto follows the live catalogue (characters → character mode, else
//try-on); the scene models are local test modes that override it on this mirror only.
export const MODE_CHOICES = ['auto', 'lucy-2.5', 'lucy-restyle-2'] as const

export type ModeChoice = (typeof MODE_CHOICES)[number]

export const isModeChoice = (value: unknown): value is ModeChoice =>
	MODE_CHOICES.includes(value as ModeChoice)

export const MODE_CHOICE_LABELS: Record<ModeChoice, string> = {
	auto: 'Follow catalogue',
	'lucy-2.5': 'World 2.5 — scene transform',
	'lucy-restyle-2': 'Restyle 2 — style transfer',
}

export const resolveMode = (choice: ModeChoice, catalogMode: CatalogMode): MirrorMode =>
	choice === 'auto' ? catalogMode : choice

export const decartModel = (mode: MirrorMode): DecartModel => (mode === 'character' ? 'lucy-2.5' : mode)

//What the dock offers: garments to wear, characters to become, or scene presets.
export type DockKind = 'garments' | 'characters' | 'presets'

export function dockKind(mode: MirrorMode): DockKind {
	if (mode === 'character') return 'characters'
	return mode.startsWith('lucy-vton') ? 'garments' : 'presets'
}

export interface ScenePreset {
	readonly label: string
	readonly prompt: string
}

//The scene models' counterpart to the garment catalogue: tappable presets in the dock.
//Lucy 2.5 is a full scene editor, so its presets tour every edit type it supports —
//style, background, add, replace, VFX — phrased in Decart's documented template style
//(specific, placement-anchored, rich visual detail, no negatives).
const WORLD_PRESETS: readonly ScenePreset[] = [
	{
		label: 'Anime',
		prompt:
			'Change the style of the video to Studio Ghibli animation with soft painted backgrounds and clean line art.',
	},
	{
		label: 'Vintage film',
		prompt:
			'Change the style of the video to a vintage 1970s film aesthetic with warm tones and slight grain.',
	},
	{
		label: 'Beach',
		prompt:
			'Change the background to a sandy beach with clear blue water and a bright sunny sky.',
	},
	{
		label: 'Runway',
		prompt:
			'Change the background to a fashion runway with a glossy black floor, bright spotlights and a crowd of photographers.',
	},
	{
		label: 'Sunglasses',
		prompt: "Add black aviator sunglasses with thin gold frames to the person's face.",
	},
	{
		label: 'Party hat',
		prompt:
			"Add a red conical hat, covered in sequins, with a white fluffy trim to the person's head.",
	},
	{
		label: 'Red leather',
		prompt:
			"Change the person's top with a fitted red leather jacket that has silver zips and a high collar.",
	},
	{
		label: 'Slime',
		prompt:
			"Add thick glossy green slime dripping down the person's head and onto their shoulders.",
	},
	{
		label: 'Blonde hair',
		prompt: "Change the person's hair color to bright blonde.",
	},
	{
		label: 'Lava hair',
		prompt: "Turn the person's hair into flowing molten lava with glowing embers.",
	},
	{
		label: 'Fireballs',
		prompt: "Add shooting fireballs to the person's hands.",
	},
]

//Restyle is style-only, so these are pure aesthetic descriptions.
const RESTYLE_PRESETS: readonly ScenePreset[] = [
	{ label: 'Ghibli', prompt: 'Studio Ghibli animation style' },
	{ label: 'Cyberpunk', prompt: 'Cyberpunk city, neon-drenched rainy night' },
	{ label: 'Watercolour', prompt: 'Watercolour painting, soft washes of pigment on textured paper' },
	{ label: 'Silent film', prompt: '1920s black-and-white silent film with grain and flicker' },
	{ label: 'Pixel art', prompt: '16-bit pixel-art video game' },
	{ label: 'Claymation', prompt: 'Claymation stop-motion world' },
	{ label: 'Comic book', prompt: 'Comic book with bold ink outlines and halftone dots' },
	{ label: 'Van Gogh', prompt: 'Swirling Van Gogh Starry Night oil painting' },
]

export const scenePresets = (mode: MirrorMode): readonly ScenePreset[] =>
	mode === 'lucy-restyle-2' ? RESTYLE_PRESETS : WORLD_PRESETS
